/*
  Abas da listagem de produtos da home.

  Todos os painéis já vêm renderizados no HTML; os inativos carregam o atributo
  `hidden`. Este elemento só alterna a visibilidade — não há fetch nem Section
  Rendering API envolvidos, então a troca é instantânea.

  Markup esperado:
  <vibrio-tabs>
    <div role="tablist">
      <button role="tab" aria-controls="painel-1" aria-selected="true">…</button>
    </div>
    <div id="painel-1" role="tabpanel" data-url="/collections/x">…</div>
  </vibrio-tabs>

  Quando o painel ativo tem `data-url`, o link marcado com
  [data-vibrio-view-all] dentro dele já aponta para a coleção certa via Liquid,
  então nada precisa ser reescrito aqui.
*/
if (!customElements.get('vibrio-tabs')) {
  customElements.define(
    'vibrio-tabs',
    class VibrioTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        if (this.tabs.length === 0) return;

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.select(tab));
          tab.addEventListener('keydown', (event) => this.onKeyDown(event));
        });

        this.syncTabIndexes();

        // No editor de temas, selecionar um bloco deve abrir a aba correspondente.
        // Guardamos a referência para remover o listener quando a seção for
        // re-renderizada, senão eles se acumulam a cada edição.
        this.onBlockSelect = (event) => {
          const tab = this.tabs.find((candidate) => candidate.dataset.blockId === event.detail.blockId);
          if (tab) this.select(tab);
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      disconnectedCallback() {
        if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      get selectedTab() {
        return this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
      }

      panelFor(tab) {
        const id = tab.getAttribute('aria-controls');
        return id ? this.querySelector(`#${CSS.escape(id)}`) : null;
      }

      select(tab) {
        if (!tab || tab === this.selectedTab) return;

        const previous = this.selectedTab;
        const previousPanel = this.panelFor(previous);
        const nextPanel = this.panelFor(tab);

        previous.setAttribute('aria-selected', 'false');
        tab.setAttribute('aria-selected', 'true');

        if (previousPanel) previousPanel.hidden = true;
        if (nextPanel) nextPanel.hidden = false;

        this.syncTabIndexes();
        this.scrollTabIntoView(tab);
      }

      syncTabIndexes() {
        // Roving tabindex: só a aba ativa entra na ordem de tabulação.
        this.tabs.forEach((tab) => {
          tab.tabIndex = tab.getAttribute('aria-selected') === 'true' ? 0 : -1;
        });
      }

      scrollTabIntoView(tab) {
        const list = tab.parentElement;
        if (!list || list.scrollWidth <= list.clientWidth) return;

        const offset = tab.offsetLeft - (list.clientWidth - tab.clientWidth) / 2;
        list.scrollTo({ left: offset, behavior: 'smooth' });
      }

      onKeyDown(event) {
        const keys = {
          ArrowRight: 1,
          ArrowLeft: -1,
        };

        if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          const target = event.key === 'Home' ? this.tabs[0] : this.tabs[this.tabs.length - 1];
          this.select(target);
          target.focus();
          return;
        }

        const step = keys[event.key];
        if (!step) return;

        event.preventDefault();
        const current = this.tabs.indexOf(event.currentTarget);
        const next = this.tabs[(current + step + this.tabs.length) % this.tabs.length];
        this.select(next);
        next.focus();
      }
    }
  );
}
